import { MillenniumWeek } from "./MillenniumWeek.js";
import { MillenniumWeekday } from "./MillenniumWeekday.js";
import { SimpleDateFormat } from "./SimpleDateFormat.js";
/**
 * Helper functions
 */
function getDaysInMonths(isLeapYear) {
    const days = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    if (isLeapYear) {
        days[1] = 29;
    }
    return days;
}
function calculateDayForYear(year) {
    return year * 365 + (year !== 0 ? Math.floor((year - 1) / 4) + 1 : 0);
}
/*
 * Main constructor
 */
export class MillenniumDate {
    static MAX_DAY_NUMBER = 24836;
    static UNDEFINED_DAY_NUMBER = 32767;
    static INVALID_DATE = -1;
    static MIN_YEAR = 2000;
    static MAX_YEAR = 2067;
    dayNumber;
    constructor(dayNumber) {
        if (dayNumber instanceof MillenniumDate) {
            dayNumber = dayNumber.getDayNumber();
        }
        this.dayNumber = dayNumber;
    }
    static create(year, month, day) {
        let resultDayNumber;
        if ((year > 100 && year < 2000) ||
            year > 2100 ||
            month > 12 ||
            month < 1 ||
            day > 31 ||
            day < 0) {
            throw new Error("Invalid date.");
        }
        year = year % 100;
        month--;
        resultDayNumber = calculateDayForYear(year);
        resultDayNumber += 30 * month;
        if (month === 1) {
            // February
            resultDayNumber++;
        }
        if (month === 2) {
            // March
            resultDayNumber--;
        }
        if (month > 1 && year % 4 === 0) {
            // February, leap year
            resultDayNumber++;
        }
        if (month > 4) {
            // May
            resultDayNumber++;
        }
        if (month > 6) {
            // July
            resultDayNumber++;
        }
        if (month > 7) {
            // August
            resultDayNumber++;
        }
        if (month > 9) {
            // October
            resultDayNumber++;
        }
        resultDayNumber += day - 1;
        return new MillenniumDate(resultDayNumber);
    }
    static today() {
        const now = new Date();
        return MillenniumDate.create(now.getFullYear(), now.getMonth() + 1, now.getDate());
    }
    static lastDateOfMonth(year, month) {
        const firstDate = MillenniumDate.create(year, month, 1);
        const daysInMonth = getDaysInMonths(firstDate.isLeapYear())[month - 1];
        return MillenniumDate.create(year, month, daysInMonth);
    }
    static fromYyyyMMdd(dateString) {
        const year = parseInt(dateString.substring(0, 4), 10);
        const month = parseInt(dateString.substring(4, 6), 10);
        const day = parseInt(dateString.substring(6, 8), 10);
        return MillenniumDate.create(year, month, day);
    }
    /**
     * Get the number of days since January 1 2001.
     *
     * @return {int} day timestamp
     */
    getDayNumber() {
        return this.dayNumber;
    }
    isBefore(otherDate) {
        return this.dayNumber < otherDate.getDayNumber();
    }
    isAfter(otherDate) {
        return this.dayNumber > otherDate.getDayNumber();
    }
    /**
     * Return the number of days between two dates.
     *
     * @param  {MillenniumDate} other date to compare with
     * @return {int}       days between dates
     */
    daysBetween(other) {
        if (this.dayNumber > other.getDayNumber()) {
            return this.dayNumber - other.getDayNumber();
        }
        return other.getDayNumber() - this.dayNumber;
    }
    isLeapYear() {
        return this.getYear() % 4 === 0;
    }
    getYear() {
        let year = Math.floor(this.dayNumber / 365);
        const days = calculateDayForYear(year);
        if (this.dayNumber < days) {
            year--;
        }
        return year + 2000;
    }
    getDayOfYear() {
        const tempDayNumber = this.getDayNumber();
        const year = Math.floor(tempDayNumber / 365);
        let daysToJan1 = calculateDayForYear(year);
        if (tempDayNumber < daysToJan1) {
            daysToJan1 = calculateDayForYear(year - 1);
        }
        return tempDayNumber - daysToJan1 + 1;
    }
    /**
     * Return the index of the month (0-11).
     * @return {int} month index
     */
    getMonth() {
        let dayOfYear = this.getDayOfYear();
        const daysInMonths = getDaysInMonths(this.isLeapYear());
        let month = -1;
        while (dayOfYear > 0) {
            month++;
            dayOfYear = dayOfYear - daysInMonths[month];
        }
        return month;
    }
    /**
     * Return the day of month.
     * @return {int} day in month
     */
    getDate() {
        let dayOfYear = this.getDayOfYear();
        const daysInMonths = getDaysInMonths(this.isLeapYear());
        const month = this.getMonth();
        let i;
        for (i = 0; i < month; i++) {
            dayOfYear = dayOfYear - daysInMonths[i];
        }
        return dayOfYear;
    }
    /**
     * Return the day of the week as an index, where 0 is Sunday and 6 is Saturday.
     * @return {int} day in week
     */
    getDay() {
        return (this.dayNumber + 6) % 7; // dayNumber = 0 was a Saturday
    }
    // Actual
    getWeek(arg1, arg2, arg3) {
        let weekObject;
        if (arg3 === undefined) {
            weekObject = new MillenniumWeek(this, arg1, arg2);
        }
        else {
            weekObject = new MillenniumWeek(this, arg2, arg3);
        }
        return weekObject.week(arg1 === true);
    }
    /**
     * Return the week number.
     * @return {int} week in year
     */
    getMillenniumWeek(firstDayOfWeek, daysInFirstWeek) {
        return new MillenniumWeek(this, firstDayOfWeek, daysInFirstWeek);
    }
    getWeekYear(firstDayOfWeek, daysInFirstWeek) {
        const year = parseInt(`${this.getWeek(true, firstDayOfWeek, daysInFirstWeek)}`.substring(0, 2), 10);
        return year + 2000;
    }
    isDayBefore(day) {
        return this.dayNumber === day?.getDayNumber?.() - 1;
    }
    isSameDayAs(other) {
        return this.dayNumber === other?.getDayNumber?.();
    }
    // Consider the weekend to always be the two last days in the locale-sorted list of weekdays.
    isWeekend(firstDayOfWeek) {
        const weekday = MillenniumWeekday.WEEKDAYS[this.getDay()];
        if (firstDayOfWeek === undefined) {
            const SATURDAY = 6;
            const SUNDAY = 0;
            return weekday.getDay() === SATURDAY || weekday.getDay() === SUNDAY;
        }
        const weekDays = MillenniumWeekday.getLocalizedWeekdayList(firstDayOfWeek);
        const SATURDAY = weekDays[5];
        const SUNDAY = weekDays[6];
        return weekday === SATURDAY || weekday === SUNDAY;
    }
    addWeeks(weeks) {
        return this.addDays(weeks * 7);
    }
    addDays(days) {
        return new MillenniumDate(this.dayNumber + days);
    }
    addMonths(months) {
        let year = this.getYear();
        let month = this.getMonth(); // Returns 0 to 11.
        month++; // Month is now 1-12
        const startMonth = month;
        month = month + months; // This is the modified month number
        if (month > 12) {
            year = year + Math.floor(month / 12);
            month = month % 12;
        }
        else if (month === 0) {
            year--;
            month = 12;
        }
        else if (month < 0) {
            if (Math.floor(Math.abs(month) / 12) === 0) {
                year--;
            }
            else {
                year = year - Math.floor(Math.abs(month) / 12); // month is negative, so floor would round up!
            }
            let monthsLeft = month % 12;
            if (-monthsLeft > startMonth) {
                year--;
                monthsLeft = 12 + monthsLeft;
            }
            else {
                monthsLeft = 12 + monthsLeft;
            }
            month = monthsLeft;
        }
        const day = this.getDate();
        return MillenniumDate.create(year, month, day);
    }
    addYears(years) {
        return MillenniumDate.create(this.getYear() + years, this.getMonth() + 1, this.getDate());
    }
    equals(obj) {
        if (this === obj) {
            return true;
        }
        if (obj === null || obj === undefined) {
            return false;
        }
        if (!(obj instanceof MillenniumDate)) {
            return false;
        }
        return this.dayNumber === obj.getDayNumber();
    }
    format(format) {
        format = format || "yyyyMMdd";
        return SimpleDateFormat.format(this, format);
    }
    toString() {
        return this.format();
    }
}
